import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { locationService } from '../../../services/locationService';
import './AdminLocations.css';

// Компонент списка локаций для панели администратора
const AdminLocationsList = () => {
  const navigate = useNavigate(); // Хук для навигации
  
  // Состояния компонента
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);          
  const [error, setError] = useState(null);              
  const [searchTerm, setSearchTerm] = useState('');      
  const [deletingId, setDeletingId] = useState(null);

  // Вспомогательная функция: обрезает длинный текст до maxLength символов
  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';                     
    if (text.length <= maxLength) return text; 
    return text.substring(0, maxLength) + '...'; 
  };

  // Загрузка локаций при монтировании (инициализации) компонента 
  useEffect(() => {
    fetchLocations();
  }, []);

  // Функция загрузки локаций из API
  const fetchLocations = async () => {
    try {
      setLoading(true);
      const locationsData = await locationService.getAllLocations();
      setLocations(Array.isArray(locationsData) ? locationsData : []); // Защита от не-массива
      setError(null);
    } catch (error) {
      console.error('Ошибка загрузки локаций:', error);
      setError('Не удалось загрузить список локаций');
    } finally {
      setLoading(false);
    }
  };

  // Функция удаления локации с подтверждением
  const handleDeleteLocation = async (id, name) => {
    console.log('НАЧАЛО УДАЛЕНИЯ');
    console.log('ID локации для удаления:', id);
    console.log('Название локации:', name);
    
    // Подтверждение через нативный диалог браузера
    const isConfirmed = window.confirm(
      `Вы уверены, что хотите удалить локацию "${name}"?\n\n` +
      `Это действие нельзя отменить.`
    );

    if (!isConfirmed) {
      console.log('Удаление отменено пользователем');
      return;
    }

    try {
      setDeletingId(id); // Блокировка кнопки удаления для этой локации
      console.log('Вызов locationService.deleteLocation с ID:', id);
      const result = await locationService.deleteLocation(id);
      console.log('Результат удаления:', result);
      
      // Удаление локации из состояния без повторного запроса
      setLocations(prevLocations => {
        const newLocations = prevLocations.filter(loc => loc.id !== id);
        console.log('Обновлённый список локаций:', newLocations);
        return newLocations;
      });
      alert('Локация успешно удалена');
    } catch (error) {
      console.error('Ошибка удаления:', error);
      console.error('Статус ошибки:', error.status);
      console.error('Сообщение ошибки:', error.message);
      alert(`Не удалось удалить локацию: ${error.message}`);
    } finally {
      setDeletingId(null); // Разблокировка кнопки
      console.log('УДАЛЕНИЕ ЗАВЕРШЕНО');
    }
  };

  // Фильтрация локаций по поисковому запросу (по названию или описанию)
  const filteredLocations = locations.filter(location =>
    location.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Получение url первого или главного фото для миниатюры
  const getPreviewPhotoUrl = (location) => {
    if (location.photos && location.photos.length > 0) {
      return location.photos[0].url; // Используем первое фото из массива
    }
    if (location.mainPhotoId && location.photos) {
      const mainPhoto = location.photos.find(p => p.id === location.mainPhotoId);
      if (mainPhoto) return mainPhoto.url;
    }
    return null; // Нет фото
  };
  
  // Состояние загрузки
  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Загрузка локаций...</p>
      </div>
    );
  }

  // Состояние ошибки
  if (error) {
    return (
      <div className="admin-error">
        <span className="error-icon">⚠️</span>
        <p>{error}</p>
        <button onClick={fetchLocations} className="btn btn-primary">
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div className="admin-locations-list">
      {/* Верхняя панель с заголовком и кнопкой создания */}
      <div className="admin-locations-list_header">
        <h2>Управление локациями</h2>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/admin/locations/add')}
        >
          + Создать локацию
        </button>
      </div>

      {/* Поле поиска с кнопкой очистки */}
      <div className="admin-locations-list_search">
        <input
          type="text"
          placeholder="Поиск по названию или описанию..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button 
            className="search-clear"
            onClick={() => setSearchTerm('')}
          >
            ✕
          </button>
        )}
      </div>

      {/* Если ничего не найдено */}
      {filteredLocations.length === 0 ? (
        <div className="admin-locations-list_empty">
          {searchTerm ? (
            <>
              <span className="empty-icon">🔍</span>
              <p>По запросу "{searchTerm}" ничего не найдено</p>
              <button 
                className="btn btn-secondary"
                onClick={() => setSearchTerm('')}
              >
                Сбросить поиск
              </button>
            </>
          ) : (
            <>
              <span className="empty-icon">📍</span>
              <p>Локации не найдены</p>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/admin/locations/add')}
              >
                Создать первую локацию
              </button>
            </>
          )}
        </div>
      ) : (
        // Таблица с локациями
        <div className="admin-locations-list_table-container">
          <table className="admin-locations-list_table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Фото</th>
                <th>Название</th>
                <th>Описание</th>
                <th>Точек</th>      {/* Количество точек в локации */}
                <th>Фото</th>       {/* Количество фото */}
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocations.map(location => {
                const previewUrl = getPreviewPhotoUrl(location);
                const pointsCount = location.points?.length || 0;
                const photosCount = location.photos?.length || 0;
                
                return (
                  <tr key={location.id}>
                    <td className="id-cell">{location.id}</td>
                    
                    {/* Миниатюра фото */}
                    <td className="photo-cell">
                      {previewUrl ? (
                        <img 
                          src={previewUrl}
                          alt={location.name}
                          className="location-thumb"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg'; // Заглушка при ошибке загрузки
                          }}
                        />
                      ) : (
                        <div className="location-thumb placeholder">
                          📷
                        </div>
                      )}
                    </td>
                    
                    <td className="name-cell">
                      <span className="location-name">{location.name}</span>
                    </td>
                    
                    <td className="description-cell">
                      {truncateText(location.description, 60)}
                    </td>
                    
                    <td className="stats-cell">
                      <span className="stat-badge points">
                        📍 {pointsCount}
                      </span>
                    </td>
                    
                    <td className="stats-cell">
                      <span className="stat-badge photos">
                        📷 {photosCount}
                      </span>
                    </td>
                    
                    {/* Кнопки действий */}
                    <td className="actions-cell">
                      <button 
                        className="btn-icon btn-edit"
                        onClick={() => navigate(`/admin/locations/edit/${location.id}`)}
                        title="Редактировать"
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-icon btn-delete"
                        onClick={() => handleDeleteLocation(location.id, location.name)}
                        disabled={deletingId === location.id} // Блокировка во время удаления
                        title="Удалить"
                      >
                        {deletingId === location.id ? '⏳' : '🗑️'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Подвал с информацией о количестве */}
      {filteredLocations.length > 0 && (
        <div className="admin-locations-list_footer">
          <span className="stats-info">
            Всего: {filteredLocations.length} из {locations.length} локаций
          </span>
        </div>
      )}
    </div>
  );
};

export default AdminLocationsList;